import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { checkAssessmentForGibberish, firstCompanyGibberishError, firstDataSourceGibberishError, firstGoalsGibberishError, goalGibberishError } from '../lib/gibberish';
import AppHeader from '../components/AppHeader';
import ReportMarkdown from '../components/ReportMarkdown';
import GoalDrilldownModal from '../components/GoalDrilldownModal';
import useGenerationJob from '../lib/useGenerationJob';
import GenerationProgress from '../components/GenerationProgress';
import { newRequestId } from '../lib/requestId';
import { Plus, Trash2, Save, Sparkles, Download, Upload, Check, XCircle, Loader2, FileText, ChevronRight, AlertCircle } from 'lucide-react';

const YESNO = ['Yes', 'No'];
const DATA_AVAIL = ['Yes', 'Partial', 'No', "Don't know"];
const DATA_MASKING_TOOL_URL = '/downloads/STArtIn_Data_Masking_Tool.xlsm';
const INR_SIGN = '\u20B9';

const initialCompany = {
  'Company Name': '', 'Address': '', 'Customer Segment': '', 'Company Profile': '',
  'Annual Revenue': '', 'No. Of Employees': '', 'Industry Segment': '', 'Product/Services': '',
  'External Vendors': '', 'Business Process Documentation': 'Yes',
  'Vendors in Core Processes': 'Yes', 'Defined Go-To-Market Process': 'Yes',
};
const initialTech = {
  'Modern Software/Cloud Platforms': 'Yes', 'Handles Intense Workloads': 'Yes',
  'Regulatory Constraints': 'Yes', 'AI Ethical Considerations': 'Yes',
};

const newGoal = () => ({
  Priority: '', 'Original Goal': '', 'Goal Description': '', Challenges: '',
  'Data Availability': '', 'Data Source Internal': '', 'Solution Fit': '', 'AI Capabilities': '',
  'Desired Outcome': '', 'Strategic Alignment': '',
  'Internal Data Requirements': '', 'External Data Requirements': '',
  validated: false,
});

const stripCurrencyFormatting = (value = '') => (
  String(value)
    .replace(new RegExp(INR_SIGN, 'g'), '')
    .replace(/\bINR\b/gi, '')
    .replace(/,/g, '')
    .trim()
);

const sanitizeNumericInput = (value, { allowDecimal = false } = {}) => {
  const stripped = stripCurrencyFormatting(value);
  if (!allowDecimal) return stripped.replace(/\D/g, '');
  const parts = stripped.replace(/[^\d.]/g, '').split('.');
  return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : parts[0];
};

const numericValue = (value) => Number(stripCurrencyFormatting(value));
const isRevenueValueValid = (value) => {
  const clean = stripCurrencyFormatting(value);
  return /^\d+(\.\d+)?$/.test(clean) && Number(clean) > 0;
};
const isEmployeeCountValid = (value) => {
  const clean = stripCurrencyFormatting(value);
  return /^\d+$/.test(clean) && Number(clean) > 0;
};
const formatRevenueValue = (value) => {
  const clean = stripCurrencyFormatting(value);
  if (!clean || !isRevenueValueValid(clean)) return clean;
  return `${INR_SIGN} ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(numericValue(clean))}`;
};

export default function Assessment() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [regenerationSource] = useState(location.state?.regeneration_source || (location.state?.regenerate ? location.state.assessment : null));
  const [company, setCompany] = useState(initialCompany);
  const [goals, setGoals] = useState([newGoal()]);
  const [dataSources, setDataSources] = useState([]);
  const [tech, setTech] = useState(initialTech);
  const [drilldownIdx, setDrilldownIdx] = useState(null);
  const [drilldownMode, setDrilldownMode] = useState('refine');
  const [validatingGoalIdx, setValidatingGoalIdx] = useState(null);
  const [goalMessages, setGoalMessages] = useState({});
  const [keptAsIsContexts, setKeptAsIsContexts] = useState({});
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState('business_overview');
  const [savedMsg, setSavedMsg] = useState('');
  const reportRef = useRef(null);
  const regenerationRequest = useRef(null);
  const generation = useGenerationJob(user ? `${user.id}:assessment` : null, (result, job) => {
    if (job.kind === 'regenerate') { nav(`/reports/${result.assessment_id}`); return; }
    setReports(result.reports);
    setAssessmentId(result.assessment_id);
    setActiveReportTab('business_overview');
    if (result.form) {
      setCompany({ ...initialCompany, ...result.form.company_information });
      const restoredGoals = (result.form.goals || []).map(g => ({ ...newGoal(), ...g, validated: !!g['Solution Fit'] }));
      setGoals(restoredGoals);
      setTech({ ...initialTech, ...result.form.technology_stack });
      setDataSources((result.form.data_sources || []).map(d => ({ ...d,
        auto: d['Data Source Type'] === 'Internal', label: d['Table Description'] || d['Data Source'] || 'Data source',
        goal_idx: restoredGoals.findIndex(g => String(g.Priority) === String(d['Goal Priority'])) })));
    }
    setStep(5);
    setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  });
  useEffect(() => { if (generation.busy || generation.job?.id) setStep(5); }, [generation.busy, generation.job?.id]);

  useEffect(() => {
    if (!user) nav('/auth');
    if (user) setCompany(c => ({
      ...c,
      'Company Name': c['Company Name'] || user.company_name || '',
      'Industry Segment': c['Industry Segment'] || user.industry_segment || '',
    }));
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const entry = location.state?.assessment;
    if (!entry?.form) return;
    setAssessmentId(entry.id || null);

    const restoredGoals = (entry.form.goals?.length ? entry.form.goals : [newGoal()]).map(g => ({ ...newGoal(), ...g, validated: !!g['Solution Fit'] }));
    setCompany({ ...initialCompany, ...(entry.form.company_information || {}) });
    setGoals(restoredGoals);
    setDataSources((entry.form.data_sources || []).map(d => {
      const auto = d['Data Source Type'] === 'Internal';
      const label = d['Table Description'] || d['Data Source'] || 'Data source';
      let goalIdx = d.goal_idx;
      if (goalIdx === undefined && d['Goal Priority']) {
        goalIdx = restoredGoals.findIndex(g => String(g.Priority) === String(d['Goal Priority']));
      }
      if (goalIdx === undefined && auto) {
        goalIdx = restoredGoals.findIndex(g => (g['Internal Data Requirements'] || '').split('\n').some(line => line.includes('| Available: Yes') && line.split('|')[0].trim() === label));
      }
      return { ...d, auto, label, goal_idx: goalIdx >= 0 ? goalIdx : undefined };
    }));
    setTech({ ...initialTech, ...(entry.form.technology_stack || {}) });
    setReports(!regenerationSource && entry.reports && Object.keys(entry.reports).length ? entry.reports : null);
    setStep(!regenerationSource && entry.reports && Object.keys(entry.reports).length ? 5 : 1);
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Auto-populate data sources from drilldown requirements or the manual
    // Is Data Available field, matching the Streamlit flow.
    const items = [];
    const generatedExternalItems = [];
    const priorityOneIdx = goals.findIndex(g => String(g.Priority) === '1');
    if (priorityOneIdx >= 0) {
      const g = goals[priorityOneIdx];
      const priorityOneNeedsData = g['Data Availability'] === 'Yes' || g['Data Availability'] === 'Partial';
      const goalItems = [];
      if (priorityOneNeedsData) {
        const internal = (g['Internal Data Requirements'] || '').split('\n').filter(Boolean);
        const external = (g['External Data Requirements'] || '').split('\n').filter(Boolean);
        internal.forEach(line => {
          if (line.includes('| Available: Yes')) {
            const label = line.split('|')[0].trim();
            goalItems.push({
              goal_idx: priorityOneIdx,
              label,
              type: 'Internal',
              auto: true,
              tableDescription: label,
              'Goal Priority': '1',
              'Associated Goal': g['Goal Description'] || '',
            });
          }
        });
        external.forEach(line => {
          const [itemPart, sourcePart = ''] = line.split('| Source:');
          const label = itemPart.trim();
          if (!label) return;
          generatedExternalItems.push({
            goal_idx: priorityOneIdx,
            label,
            type: 'External',
            auto: false,
            generatedFromGoal: true,
            'Data Source Type': 'External',
            'Goal Priority': '1',
            'Associated Goal': g['Goal Description'] || '',
            'Source': 'Excel / CSV Files',
            'Data Source': sourcePart.trim(),
            'Storage': '',
            'Table Name/URL': '',
            'Table Description': '',
            'Record Count': '',
            'Available Since': new Date().toISOString().slice(0, 10),
            'Policies': 'Yes',
            'Security Measures': 'Yes',
            'Error-Free Data': 'Yes',
            'External URL': sourcePart.trim().startsWith('http') ? sourcePart.trim() : '',
          });
        });
        if (
          goalItems.length === 0 &&
          generatedExternalItems.length === 0 &&
          g['Data Source Internal'] === 'Yes'
        ) {
          goalItems.push({
            goal_idx: priorityOneIdx,
            label: 'Priority 1 internal data source',
            type: 'Internal',
            auto: true,
            'Goal Priority': '1',
            'Associated Goal': g['Goal Description'] || '',
            tableDescription: g['Goal Description'] || `Data required for priority 1 goal`,
          });
        }
      }
      items.push(...goalItems);
    }
    setDataSources(prev => {
      const priorityOneGoal = priorityOneIdx >= 0 ? goals[priorityOneIdx] : null;
      const priorityOneNeedsData = priorityOneGoal && (priorityOneGoal['Data Availability'] === 'Yes' || priorityOneGoal['Data Availability'] === 'Partial');
      const externals = priorityOneNeedsData
        ? prev.filter(d => !d.auto && !d.generatedFromGoal).map(d => ({
          ...d,
          'Goal Priority': '1',
          'Associated Goal': priorityOneGoal?.['Goal Description'] || d['Associated Goal'] || '',
          goal_idx: priorityOneIdx,
        }))
        : [];
      const newAuto = items.map((it) => {
        const existing = prev.find(p => p.auto && p.label === it.label && p.goal_idx === it.goal_idx);
        return existing ? {
          ...existing,
          label: it.label,
          goal_idx: it.goal_idx,
          'Goal Priority': it['Goal Priority'],
          'Associated Goal': it['Associated Goal'],
        } : {
          ...it, 'Data Source Type': 'Internal', 'Policies': 'Yes', 'Security Measures': 'Yes',
          'Source': 'Relational Database (SQL Server, MySQL, PostgreSQL)', 'Data Source': '', 'Storage': '',
          'Table Name/URL': '', 'Table Description': '', 'Record Count': '',
          'Available Since': new Date().toISOString().slice(0, 10), 'Error-Free Data': 'Yes', 'External URL': '',
        };
      });
      const newGeneratedExternal = generatedExternalItems.map((it) => {
        const existing = prev.find(p => p.generatedFromGoal && p.label === it.label && p.goal_idx === it.goal_idx);
        return existing ? {
          ...existing,
          label: it.label,
          goal_idx: it.goal_idx,
          'Goal Priority': it['Goal Priority'],
          'Associated Goal': it['Associated Goal'],
          'Data Source Type': 'External',
        } : it;
      });
      return [...newAuto, ...newGeneratedExternal, ...externals];
    });
  // eslint-disable-next-line
  }, [JSON.stringify(goals.map(g => [g.Priority, g['Internal Data Requirements'], g['External Data Requirements'], g['Data Availability'], g['Data Source Internal'], g['Goal Description']]))]);

  const addGoal = () => setGoals(prev => {
    if (prev.length >= 5) return prev;
    const used = new Set(prev.map(g => String(g.Priority)).filter(Boolean));
    const priority = [1, 2, 3, 4, 5].find(n => !used.has(String(n)));
    return [...prev, { ...newGoal(), Priority: priority ? String(priority) : '' }];
  });
  const removeGoal = (i) => setGoals(prev => prev.filter((_, j) => j !== i));
  const updateGoal = (i, k, v) => setGoals(prev => prev.map((g, j) => {
    if (j !== i) return g;
    const next = { ...g, [k]: v };
    if (k === 'Data Availability' && v !== 'Yes' && v !== 'Partial') {
      next['Data Source Internal'] = '';
    }
    if (k === 'Priority' && v !== '1') {
      next['Data Source Internal'] = '';
    }
    return next;
  }));

  const addExternalDS = () => setDataSources(prev => [...prev, {
    auto: false, 'Data Source Type': 'External', 'Policies': 'Yes', 'Security Measures': 'Yes',
    'Source': 'Excel / CSV Files', 'Data Source': '', 'Storage': '', 'Table Name/URL': '',
    'Table Description': '', 'Record Count': '', 'Available Since': new Date().toISOString().slice(0, 10),
    'Error-Free Data': 'Yes', 'External URL': '',
    'Goal Priority': '1',
    'Associated Goal': goals.find(g => String(g.Priority) === '1')?.['Goal Description'] || '',
    goal_idx: goals.findIndex(g => String(g.Priority) === '1'),
  }]);
  const removeDS = (i) => setDataSources(prev => prev.filter((_, j) => j !== i));
  const updateDS = (i, k, v) => setDataSources(prev => prev.map((d, j) => j === i ? { ...d, [k]: v } : d));
  const clearDSUpload = (i) => setDataSources(prev => prev.map((d, j) => (
    j === i ? { ...d, uploadFile: null, 'Uploaded Files': [] } : d
  )));

  const serializeDataSource = (d) => {
    const { uploadFile, auto, label, goal_idx, type, tableDescription, generatedFromGoal, ...rest } = d;
    const uploadMeta = uploadFile
      ? [{ original_filename: uploadFile.name, content_type: uploadFile.type, file_size_bytes: uploadFile.size }]
      : (d['Uploaded Files'] || []);
    return { ...rest, 'Uploaded Files': uploadMeta };
  };

  const buildPayload = () => ({
    company_information: company,
    goals: regenerationSource?.form.goals || goals.map((g, i) => ({ ...g, Priority: g.Priority || String(i + 1) })),
    data_sources: dataSources.map(serializeDataSource),
    technology_stack: tech,
  });

  const fileToBase64 = async (file) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return window.btoa(binary);
  };

  const buildUploadedFiles = async () => {
    const uploads = [];
    for (let i = 0; i < dataSources.length; i += 1) {
      const file = dataSources[i].uploadFile;
      if (!file) continue;
      uploads.push({
        data_source_index: i,
        filename: file.name,
        content_type: file.type,
        base64: await fileToBase64(file),
      });
    }
    return uploads;
  };

  const isWeakAnswer = (value = '') => {
    const text = value.trim().toLowerCase();
    return !text || text.length < 8 || ['no idea', 'no clue', "don't know", 'dont know', 'you tell me', 'n/a', 'na'].includes(text);
  };

  const goalCheckContext = (g) => `${(g?.['Goal Description'] || '').trim()}\n${(g?.Challenges || '').trim()}`;

  const evaluateGoalForDrilldown = async (idx, { automatic = false } = {}) => {
    const g = goals[idx];
    const context = goalCheckContext(g);
    const junkError = goalGibberishError(g, idx);
    if (!g['Goal Description']?.trim() || !g.Challenges?.trim()) {
      if (!automatic) {
        setGoalMessages(m => ({ ...m, [idx]: { type: 'warn', text: 'Enter both the goal and the challenge before running AI Drilldown.' } }));
      }
      return;
    }
    if (junkError) {
      setGoalMessages(m => ({ ...m, [idx]: { type: 'warn', text: junkError.text } }));
      return;
    }
    if (isWeakAnswer(g.Challenges)) {
      setGoalMessages(m => ({ ...m, [idx]: { type: 'warn', text: 'Please describe the main challenge more specifically before drilling down the goal.' } }));
      return;
    }
    if (automatic && keptAsIsContexts[idx] === context) return;

    setValidatingGoalIdx(idx);
    setGoalMessages(m => ({ ...m, [idx]: { type: 'info', text: 'Checking whether this goal is already AI-actionable...' } }));
    try {
      const r = await api.post('/llm/validate-goal', { goal: g['Goal Description'], challenges: g.Challenges });
      if (r.data?.is_actionable) {
        updateGoal(idx, 'validated', true);
        setGoalMessages(m => ({ ...m, [idx]: null }));
        setDrilldownMode('data-only');
        setDrilldownIdx(idx);
        return;
      }
      setGoalMessages(m => ({ ...m, [idx]: null }));
      setDrilldownMode('refine');
      setDrilldownIdx(idx);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && detail) {
        const message = Array.isArray(detail) ? detail.join('\n') : String(detail);
        setGoalMessages(m => ({ ...m, [idx]: { type: 'warn', text: message } }));
        return;
      }
      setGoalMessages(m => ({ ...m, [idx]: null }));
      setDrilldownMode('refine');
      setDrilldownIdx(idx);
    } finally {
      setValidatingGoalIdx(null);
    }
  };

  const openDrilldown = (idx) => evaluateGoalForDrilldown(idx, { automatic: false });
  const autoEvaluateGoal = (idx) => evaluateGoalForDrilldown(idx, { automatic: true });

  const keepGoalAsIs = (idx) => {
    const g = goals[idx];
    const originalGoal = g['Original Goal'] || g['Goal Description'];
    if (originalGoal) {
      updateGoal(idx, 'Goal Description', originalGoal);
    }
    updateGoal(idx, 'Original Goal', '');
    updateGoal(idx, 'Solution Fit', '');
    updateGoal(idx, 'AI Capabilities', '');
    updateGoal(idx, 'Desired Outcome', '');
    updateGoal(idx, 'Strategic Alignment', '');
    updateGoal(idx, 'Internal Data Requirements', '');
    updateGoal(idx, 'External Data Requirements', '');
    updateGoal(idx, 'validated', false);
    setKeptAsIsContexts(prev => ({
      ...prev,
      [idx]: `${String(originalGoal || '').trim()}\n${(g.Challenges || '').trim()}`,
    }));
    setGoalMessages(m => ({ ...m, [idx]: { type: 'info', text: 'Goal kept as-is. You can still run AI Drilldown later if you want to refine it.' } }));
    setDrilldownIdx(null);
  };

  const deriveDataAvailability = (internal = '', external = '') => {
    const lines = internal.split('\n').filter(Boolean);
    const externalLines = external.split('\n').filter(Boolean);
    const yesCount = lines.filter(line => line.includes('| Available: Yes')).length;
    const noCount = lines.filter(line => line.includes('| Available: No')).length;
    if (yesCount > 0 && noCount === 0 && externalLines.length === 0) return 'Yes';
    if (yesCount > 0) return 'Partial';
    if (externalLines.length > 0 && lines.length === 0) return 'Yes';
    if (externalLines.length > 0) return 'Partial';
    if (lines.length > 0) return 'No';
    return "Don't know";
  };

  const saveDraft = async () => {
    setSavedMsg('');
    try {
      const uploaded_files = await buildUploadedFiles();
      await api.post('/assessments/save', { status: 'draft', form: buildPayload(), uploaded_files, regeneration_source_id: regenerationSource?.id });
      setSavedMsg('Draft saved');
    } catch { setSavedMsg('Save failed'); }
  };

  const generate = async () => {
    if (generating || generation.busy) return;
    const inputError = checkAssessmentForGibberish(buildPayload());
    if (inputError) {
      alert(`Please revise this input before generating the report. ${inputError.text}`);
      return;
    }
    setGenerating(true);
    try {
      const uploaded_files = await buildUploadedFiles();
      const form = buildPayload();
      if (regenerationSource) {
        form.goals = regenerationSource.form.goals;
        const signature = JSON.stringify({ form, uploaded_files });
        if (regenerationRequest.current?.signature !== signature) {
          regenerationRequest.current = { signature, id: newRequestId() };
        }
        await generation.start({ kind: 'regenerate', assessment_id: regenerationSource.id,
          form, uploaded_files, regeneration_request_id: regenerationRequest.current.id,
        });
        return;
      }
      await generation.start({ kind: 'synopsis', form, uploaded_files });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.join('\n') : (typeof detail === 'object' && detail ? JSON.stringify(detail) : detail);
      alert('Generation failed: ' + (message || e.message));
    } finally { setGenerating(false); }
  };

  const hasText = (value) => String(value ?? '').trim().length > 0;
  const companyTextError = firstCompanyGibberishError(company);
  const goalTextErrors = goals.map((g, i) => goalGibberishError(g, i));
  const goalsTextError = firstGoalsGibberishError(goals);
  const dataSourceTextError = firstDataSourceGibberishError(dataSources);
  const assessmentTextError = companyTextError || goalsTextError || dataSourceTextError;
  const companyComplete = Object.keys(initialCompany).every(k => hasText(company[k])) &&
    isRevenueValueValid(company['Annual Revenue']) &&
    isEmployeeCountValid(company['No. Of Employees']) &&
    !companyTextError;
  const priorityValues = goals.map(g => String(g.Priority)).filter(Boolean);
  const prioritiesAreUnique = new Set(priorityValues).size === priorityValues.length;
  const hasPriorityOne = goals.some(g => String(g.Priority) === '1');
  const needsManualInternalSourceDecision = (g) =>
    String(g.Priority) === '1' &&
    !hasText(g['Internal Data Requirements']) &&
    !hasText(g['External Data Requirements']) &&
    (g['Data Availability'] === 'Yes' || g['Data Availability'] === 'Partial');
  const goalsComplete = goals.length > 0 && goals.every(g =>
    hasText(g.Priority) &&
    hasText(g['Goal Description']) &&
    hasText(g.Challenges) &&
    hasText(g['Data Availability']) &&
    (!needsManualInternalSourceDecision(g) || hasText(g['Data Source Internal']))
  ) && prioritiesAreUnique && hasPriorityOne && !goalsTextError;
  const priorityOneGoal = goals.find(g => String(g.Priority) === '1');
  const dataAvailAny = priorityOneGoal?.['Data Availability'] === 'Yes' || priorityOneGoal?.['Data Availability'] === 'Partial';
  const requiredDataSourceFields = [
    'Source', 'Data Source', 'Storage', 'Table Name/URL', 'Table Description',
    'Record Count', 'Available Since', 'Policies', 'Security Measures', 'Error-Free Data',
  ];
  const hasDataSourceUpload = (d) => !!d.uploadFile || (d['Uploaded Files'] || []).length > 0;
  const dataSourcesComplete = (!dataAvailAny || (
    dataSources.length > 0 &&
    dataSources.every(d =>
      requiredDataSourceFields.every(k => hasText(d[k])) &&
      (d.auto || hasDataSourceUpload(d))
    )
  )) && !dataSourceTextError;
  const techComplete = Object.keys(initialTech).every(k => hasText(tech[k]));
  const readyToGenerate = companyComplete && goalsComplete && dataSourcesComplete && techComplete && !assessmentTextError;

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: progress sidebar */}
          <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">/ Assessment</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mb-6">AI Readiness</h2>
            <nav className="space-y-1" data-testid="step-nav">
              {[
                { n: 1, t: 'Company Information' },
                { n: 2, t: 'Goals & Challenges', disabled: !companyComplete },
                { n: 3, t: 'Data Sources', disabled: !companyComplete || !goalsComplete || !dataAvailAny },
                { n: 4, t: 'Technology Stack', disabled: !companyComplete || !goalsComplete || (dataAvailAny && !dataSourcesComplete) },
                { n: 5, t: 'Generate Report', disabled: !readyToGenerate },
              ].map(s => (
                <button
                  key={s.n}
                  onClick={() => !s.disabled && setStep(s.n)}
                  disabled={s.disabled}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm transition-all ${step === s.n ? 'bg-[#002FA7] text-white' : s.disabled ? 'text-zinc-300 cursor-not-allowed' : 'text-zinc-700 hover:bg-zinc-100'}`}
                  data-testid={`step-${s.n}`}
                >
                  <span className={`font-mono text-xs ${step === s.n ? 'text-white' : 'text-zinc-400'}`}>0{s.n}</span>
                  <span>{s.t}</span>
                  {step === s.n && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
                </button>
              ))}
            </nav>

            <div className="mt-8 pt-6 border-t border-zinc-200 space-y-2">
              <button onClick={saveDraft} className="w-full text-xs flex items-center justify-center gap-2 border border-zinc-300 px-4 py-2.5 hover:bg-zinc-50 transition-colors" data-testid="save-draft-btn">
                <Save className="w-3.5 h-3.5" /> Save Draft
              </button>
              {savedMsg && <div role={savedMsg === 'Save failed' ? 'alert' : 'status'} className={`text-[11px] flex items-center gap-1 ${savedMsg === 'Save failed' ? 'text-red-600' : 'text-green-600'}`}>
                {savedMsg === 'Save failed' ? <XCircle className="w-3 h-3 shrink-0" /> : <Check className="w-3 h-3 shrink-0" />}
                {savedMsg === 'Save failed' ? 'Save failed. Please try again.' : savedMsg}
              </div>}
            </div>
          </aside>

          {/* Right: form */}
          <main className="lg:col-span-9 min-w-0">
            {step === 1 && <CompanyStep company={company} setCompany={setCompany} onNext={() => setStep(2)} nextDisabled={!companyComplete} textError={companyTextError} />}
            {step === 2 && regenerationSource && <LockedGoals goals={regenerationSource.form.goals} onNext={() => setStep(dataAvailAny ? 3 : 4)} onBack={() => setStep(1)} />}
            {step === 2 && !regenerationSource && <GoalsStep goals={goals} updateGoal={updateGoal} addGoal={addGoal} removeGoal={removeGoal} openDrilldown={openDrilldown} onGoalReadyBlur={autoEvaluateGoal} validatingGoalIdx={validatingGoalIdx} goalMessages={goalMessages} goalTextErrors={goalTextErrors} textError={goalsTextError} onNext={() => setStep(dataAvailAny ? 3 : 4)} onBack={() => setStep(1)} nextDisabled={!goalsComplete} prioritiesAreUnique={prioritiesAreUnique} hasPriorityOne={hasPriorityOne} />}
            {step === 3 && <DataStep dataSources={dataSources} updateDS={updateDS} addExternal={addExternalDS} removeDS={removeDS} clearDSUpload={clearDSUpload} onNext={() => setStep(4)} onBack={() => setStep(2)} nextDisabled={!dataSourcesComplete} textError={dataSourceTextError} />}
            {step === 4 && <TechStep tech={tech} setTech={setTech} onNext={() => setStep(5)} onBack={() => setStep(dataAvailAny ? 3 : 2)} nextDisabled={!techComplete} />}
            {step === 5 && (
              <GenerateStep generation={generation} onPayment={() => nav(`/payment/${assessmentId}`)} paymentAvailable={!!assessmentId} generating={generating || generation.busy} reports={reports} onGenerate={generate} onBack={() => setStep(4)} activeTab={activeReportTab} setActiveTab={setActiveReportTab} reportRef={reportRef} canGenerate={readyToGenerate} inputError={assessmentTextError} />
            )}
          </main>
        </div>
      </div>

      {drilldownIdx !== null && (
        <GoalDrilldownModal
          goalIndex={drilldownIdx}
          goal={goals[drilldownIdx]}
          mode={drilldownMode}
          onClose={() => setDrilldownIdx(null)}
          onKeepAsIs={() => keepGoalAsIs(drilldownIdx)}
          onComplete={(payload) => {
            const g = goals[drilldownIdx];
            const dataOnly = payload.data_only || drilldownMode === 'data-only';
            if (!dataOnly) {
              updateGoal(drilldownIdx, 'Original Goal', g['Original Goal'] || g['Goal Description']);
              updateGoal(drilldownIdx, 'Goal Description', payload.refined_goal);
              updateGoal(drilldownIdx, 'Solution Fit', 'AI-assisted solution');
              updateGoal(drilldownIdx, 'AI Capabilities', payload.ai_capabilities);
              updateGoal(drilldownIdx, 'Desired Outcome', payload.kpi);
              updateGoal(drilldownIdx, 'Strategic Alignment', payload.priority);
            }
            updateGoal(drilldownIdx, 'Internal Data Requirements', payload.internal);
            updateGoal(drilldownIdx, 'External Data Requirements', payload.external);
            updateGoal(drilldownIdx, 'Data Availability', deriveDataAvailability(payload.internal, payload.external));
            updateGoal(drilldownIdx, 'Data Source Internal', payload.internal?.includes('| Available: Yes') ? 'Yes' : payload.external ? 'No' : '');
            updateGoal(drilldownIdx, 'validated', true);
            setDrilldownIdx(null);
          }}
        />
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-zinc-700 mb-1.5 block uppercase tracking-wider">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all";
const inputErrorCls = "border-red-400 focus:border-red-500 focus:ring-red-500/20";

function ValidationNotice({ text, className = '' }) {
  if (!text) return null;
  return (
    <div className={`border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2 ${className}`}>
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function StepHeader({ num, title, sub }) {
  return (
    <div className="mb-8 pb-6 border-b border-zinc-200">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-mono">Step {String(num).padStart(2, '0')} / 05</div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">{title}</h1>
      {sub && <p className="text-sm text-zinc-600 mt-2 max-w-xl">{sub}</p>}
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel = 'Continue', nextDisabled = false, testid = 'step-next' }) {
  return (
    <div className="mt-10 pt-6 border-t border-zinc-200 flex justify-between">
      {onBack ? <button onClick={onBack} className="text-sm text-zinc-600 hover:text-zinc-950 px-4 py-2.5 transition-colors" data-testid="step-back">← Back</button> : <div />}
      <button onClick={onNext} disabled={nextDisabled} className="bg-[#002FA7] text-white px-6 py-2.5 hover:bg-[#00227A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2" data-testid={testid}>
        {nextLabel} →
      </button>
    </div>
  );
}

function CompanyStep({ company, setCompany, onNext, nextDisabled, textError }) {
  const set = (k) => (v) => setCompany({ ...company, [k]: v });
  const revenueInvalid = company['Annual Revenue'] && !isRevenueValueValid(company['Annual Revenue']);
  const employeesInvalid = company['No. Of Employees'] && !isEmployeeCountValid(company['No. Of Employees']);
  const fieldHasError = (key) => textError?.field === key;
  return (
    <div className="animate-reveal" data-testid="step-company">
      <StepHeader num={1} title="Company Information" sub="Tell us about your organization. This grounds the AI analysis." />
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Company Name"><input className={`${inputCls} ${fieldHasError('Company Name') ? inputErrorCls : ''}`} value={company['Company Name']} onChange={e => set('Company Name')(e.target.value)} data-testid="co-name" /></Field>
        <Field label="Address"><input className={inputCls} value={company['Address']} onChange={e => set('Address')(e.target.value)} data-testid="co-address" /></Field>
        <Field label="Industry Segment"><input className={`${inputCls} ${fieldHasError('Industry Segment') ? inputErrorCls : ''}`} value={company['Industry Segment']} onChange={e => set('Industry Segment')(e.target.value)} data-testid="co-industry" /></Field>
        <Field label="Customer Segment"><input className={`${inputCls} ${fieldHasError('Customer Segment') ? inputErrorCls : ''}`} value={company['Customer Segment']} onChange={e => set('Customer Segment')(e.target.value)} data-testid="co-customer" /></Field>
        <Field label="Annual Revenue">
          <input
            className={`${inputCls} ${revenueInvalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
            value={company['Annual Revenue']}
            onFocus={() => set('Annual Revenue')(stripCurrencyFormatting(company['Annual Revenue']))}
            onChange={e => set('Annual Revenue')(sanitizeNumericInput(e.target.value, { allowDecimal: true }))}
            onBlur={e => set('Annual Revenue')(formatRevenueValue(e.target.value))}
            placeholder="e.g. 5000000"
            inputMode="decimal"
            data-testid="co-revenue"
          />
          {revenueInvalid && <p className="mt-1.5 text-xs text-red-600">Enter annual revenue as a positive number.</p>}
        </Field>
        <Field label="No. of Employees">
          <input
            className={`${inputCls} ${employeesInvalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
            value={company['No. Of Employees']}
            onChange={e => set('No. Of Employees')(sanitizeNumericInput(e.target.value))}
            placeholder="e.g. 50"
            inputMode="numeric"
            data-testid="co-employees"
          />
          {employeesInvalid && <p className="mt-1.5 text-xs text-red-600">Enter number of employees as a positive whole number.</p>}
        </Field>
        <div className="md:col-span-2"><Field label="Company Profile"><textarea className={`${inputCls} ${fieldHasError('Company Profile') ? inputErrorCls : ''}`} rows={3} value={company['Company Profile']} onChange={e => set('Company Profile')(e.target.value)} data-testid="co-profile" /></Field></div>
        <div className="md:col-span-2"><Field label="Products / Services"><textarea className={`${inputCls} ${fieldHasError('Product/Services') ? inputErrorCls : ''}`} rows={3} value={company['Product/Services']} onChange={e => set('Product/Services')(e.target.value)} data-testid="co-products" /></Field></div>
        <Field label="External Vendors">
          <select className={inputCls} value={company['External Vendors']} onChange={e => set('External Vendors')(e.target.value)} data-testid="co-vendors">
            <option value="">Select</option>
            {YESNO.map(v => <option key={v}>{v}</option>)}
          </select>
        </Field>
        {['Business Process Documentation', 'Vendors in Core Processes', 'Defined Go-To-Market Process'].map(k => (
          <Field key={k} label={k}><select className={inputCls} value={company[k]} onChange={e => set(k)(e.target.value)} data-testid={`co-${k.replace(/\s+/g, '-').toLowerCase()}`}>{YESNO.map(v => <option key={v}>{v}</option>)}</select></Field>
        ))}
      </div>
      <ValidationNotice text={textError?.text} className="mt-5" />
      <NavButtons onNext={onNext} nextDisabled={nextDisabled} testid="step1-next" />
    </div>
  );
}

function GoalsStep({ goals, updateGoal, addGoal, removeGoal, openDrilldown, onGoalReadyBlur, validatingGoalIdx, goalMessages, goalTextErrors = [], textError, onNext, onBack, nextDisabled, prioritiesAreUnique, hasPriorityOne }) {
  return (
    <div className="animate-reveal" data-testid="step-goals">
      <StepHeader num={2} title="Goals & Challenges" sub="Define up to 5 prioritised goals. Use AI Drilldown to refine vague goals into AI-actionable ones." />
      {(!prioritiesAreUnique || !hasPriorityOne) && (
        <div className="mb-5 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{!prioritiesAreUnique ? 'Each goal must have a unique priority.' : 'One goal must be marked as Priority 1 because data collection is based on the top-priority goal.'}</span>
        </div>
      )}
      <ValidationNotice text={textError?.text} className="mb-5" />
      <div className="space-y-6">
        {goals.map((g, i) => (
          <div key={i} className="border border-zinc-200 p-6" data-testid={`goal-card-${i}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-[#002FA7] uppercase tracking-wider">Goal #{i + 1}</span>
                {g.validated && <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-2 py-0.5">AI-refined</span>}
              </div>
              {goals.length > 1 && <button onClick={() => removeGoal(i)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`remove-goal-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Priority">
                <select className={inputCls} value={g.Priority} onChange={e => updateGoal(i, 'Priority', e.target.value)} data-testid={`goal-${i}-priority`}>
                  <option value="">Select</option>
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n} disabled={goals.some((other, j) => j !== i && String(other.Priority) === String(n))}>
                      {n}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="flex items-end">
                <button onClick={() => openDrilldown(i)} disabled={!g['Goal Description']?.trim() || !g.Challenges?.trim() || validatingGoalIdx === i} className="w-full bg-zinc-950 text-white px-4 py-2.5 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center gap-2" data-testid={`goal-${i}-drilldown`}>
                  {validatingGoalIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Drilldown
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Field label="Goal Description"><textarea rows={3} className={`${inputCls} ${goalTextErrors[i]?.field === 'Goal Description' ? inputErrorCls : ''}`} value={g['Goal Description']} onChange={e => updateGoal(i, 'Goal Description', e.target.value)} placeholder="e.g., Improve customer retention by reducing churn" data-testid={`goal-${i}-desc`} /></Field>
              <Field label="Challenges"><textarea rows={3} className={`${inputCls} ${goalTextErrors[i]?.field === 'Challenges' ? inputErrorCls : ''}`} value={g.Challenges} onChange={e => updateGoal(i, 'Challenges', e.target.value)} onBlur={() => onGoalReadyBlur(i)} placeholder="e.g., No unified view of customer interactions" data-testid={`goal-${i}-challenges`} /></Field>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Field label="Is Data Available?">
                <select className={inputCls} value={g['Data Availability']} onChange={e => updateGoal(i, 'Data Availability', e.target.value)} disabled={!!g['Internal Data Requirements']} data-testid={`goal-${i}-data-avail`}>
                  <option value="">Select</option>
                  {DATA_AVAIL.map(v => <option key={v}>{v}</option>)}
                </select>
              </Field>
              {String(g.Priority) === '1' && !g['Internal Data Requirements'] && !g['External Data Requirements'] && (g['Data Availability'] === 'Yes' || g['Data Availability'] === 'Partial') && (
                <Field label="Is Data Source Internal?">
                  <select className={inputCls} value={g['Data Source Internal']} onChange={e => updateGoal(i, 'Data Source Internal', e.target.value)} data-testid={`goal-${i}-data-source-internal`}>
                    <option value="">Select</option>
                    {YESNO.map(v => <option key={v}>{v}</option>)}
                  </select>
                </Field>
              )}
            </div>
            {String(g.Priority) !== '1' && (g['Data Availability'] === 'Yes' || g['Data Availability'] === 'Partial') && (
              <div className="mt-3 text-xs text-zinc-500">
                Data-source details will be collected only for the Priority 1 goal.
              </div>
            )}
            {goalMessages[i]?.text && (
              <div className={`mt-4 border p-3 text-xs flex gap-2 ${goalMessages[i].type === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : goalMessages[i].type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{goalMessages[i].text}</span>
              </div>
            )}
            {g['Solution Fit'] && (
              <div className="mt-4 bg-zinc-50 border border-zinc-200 p-4 text-xs space-y-1.5">
                {g['Original Goal'] && <div><span className="text-zinc-500">Original: </span>{g['Original Goal']}</div>}
                <div><span className="text-zinc-500">Solution fit: </span>{g['Solution Fit']}</div>
                <div><span className="text-zinc-500">AI capabilities: </span>{g['AI Capabilities']}</div>
                <div><span className="text-zinc-500">Desired outcome: </span>{g['Desired Outcome']}</div>
              </div>
            )}
          </div>
        ))}
        {goals.length < 5 && (
          <button onClick={addGoal} className="w-full border-2 border-dashed border-zinc-300 px-6 py-4 text-sm text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex items-center justify-center gap-2" data-testid="add-goal-btn">
            <Plus className="w-4 h-4" /> Add Goal (max 5)
          </button>
        )}
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={nextDisabled} testid="step2-next" />
    </div>
  );
}

function DataStep({ dataSources, updateDS, addExternal, removeDS, clearDSUpload, onNext, onBack, nextDisabled, textError }) {
  return (
    <div className="animate-reveal" data-testid="step-data">
      <StepHeader num={3} title="Data Sources" sub="Data sources are collected only for the Priority 1 goal. Add internal or external sources needed for that implementation." />
      <ValidationNotice text={textError?.text} className="mb-5" />
      <div className="space-y-5">
        {dataSources.length === 0 && (
          <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
            No internal data source card was created. Add an external data source below if the Priority 1 goal depends on external data.
          </div>
        )}
        {dataSources.map((d, i) => (
          <div key={i} className="border border-zinc-200 p-6" data-testid={`ds-card-${i}`}>
            <div className="flex justify-between items-start gap-4 mb-4">
              <div>
                <div className="font-mono text-xs text-[#002FA7] uppercase tracking-wider">{d.auto ? `Internal: ${d.label}` : `External Data Source #${i + 1}`}</div>
                <div className="mt-1 text-xs text-zinc-500">
                  Associated with Priority {d['Goal Priority'] || '1'} goal{d['Associated Goal'] ? `: ${d['Associated Goal']}` : ''}
                </div>
              </div>
              {!d.auto && <button onClick={() => removeDS(i)} className="text-zinc-400 hover:text-red-500" data-testid={`remove-ds-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="System / Database Type"><select className={inputCls} value={d['Source']} onChange={e => updateDS(i, 'Source', e.target.value)} data-testid={`ds-${i}-sys`}>{['Relational Database (SQL Server, MySQL, PostgreSQL)', 'Cloud Data Warehouse (Snowflake, BigQuery, Redshift)', 'ERP System (SAP, Oracle, Tally)', 'Excel / CSV Files'].map(o => <option key={o}>{o}</option>)}</select></Field>
              <Field label="Database / System Name"><input className={inputCls} value={d['Data Source']} onChange={e => updateDS(i, 'Data Source', e.target.value)} data-testid={`ds-${i}-name`} /></Field>
              <Field label="Storage Location"><input className={inputCls} value={d['Storage']} onChange={e => updateDS(i, 'Storage', e.target.value)} placeholder="e.g. AWS RDS" data-testid={`ds-${i}-storage`} /></Field>
              <Field label="Table Name(s)"><input className={inputCls} value={d['Table Name/URL']} onChange={e => updateDS(i, 'Table Name/URL', e.target.value)} data-testid={`ds-${i}-table`} /></Field>
              <Field label="Approximate Record Count"><input className={inputCls} value={d['Record Count']} onChange={e => updateDS(i, 'Record Count', e.target.value)} placeholder="e.g. 50000" data-testid={`ds-${i}-records`} /></Field>
              <Field label="What data does this table have?">
                <textarea
                  rows={3}
                  className={`${inputCls} min-h-[7.5rem] resize-y ${textError?.text?.startsWith(`Data source ${i + 1}`) ? inputErrorCls : ''}`}
                  value={d['Table Description']}
                  onChange={e => updateDS(i, 'Table Description', e.target.value)}
                  placeholder="e.g. This table contains export order details including order ID, product, destination country, order date, quantity, value, and delivery status."
                  data-testid={`ds-${i}-description`}
                />
              </Field>
              <Field label="Available Since"><input type="date" className={inputCls} value={d['Available Since']} onChange={e => updateDS(i, 'Available Since', e.target.value)} data-testid={`ds-${i}-since`} /></Field>
              <Field label="Privacy & Compliance"><select className={inputCls} value={d['Policies']} onChange={e => updateDS(i, 'Policies', e.target.value)}>{YESNO.map(v => <option key={v}>{v}</option>)}</select></Field>
              <Field label="Security Measures"><select className={inputCls} value={d['Security Measures']} onChange={e => updateDS(i, 'Security Measures', e.target.value)}>{YESNO.map(v => <option key={v}>{v}</option>)}</select></Field>
              <Field label="Error-Free Data"><select className={inputCls} value={d['Error-Free Data']} onChange={e => updateDS(i, 'Error-Free Data', e.target.value)}>{YESNO.map(v => <option key={v}>{v}</option>)}</select></Field>
              {!d.auto && <Field label="External URL"><input className={inputCls} value={d['External URL']} onChange={e => updateDS(i, 'External URL', e.target.value)} placeholder="https://..." /></Field>}
              <div className="md:col-span-2 flex flex-col gap-3 border-y border-zinc-100 py-4 sm:flex-row sm:items-center">
                <a
                  href={DATA_MASKING_TOOL_URL}
                  download
                  className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 bg-zinc-950 px-4 text-xs font-medium text-white transition-colors hover:bg-zinc-800 sm:w-[16rem] sm:min-w-[16rem] sm:max-w-[16rem]"
                  data-testid={`ds-${i}-masking-tool`}
                >
                  <Download className="h-3.5 w-3.5" /> Download Data Masking Tool
                </a>
                <p className="text-xs uppercase leading-relaxed tracking-wide text-zinc-500">
                  Please use this data masking tool to mask sensitive data before upload if you do not want to share actual data.
                </p>
              </div>
              <div className="md:col-span-2">
                <Field label={d.auto ? 'Upload Sample CSV or Excel File (Optional)' : 'Upload Sample CSV or Excel File'}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label htmlFor={`ds-${i}-upload`} className="inline-flex h-11 w-full shrink-0 cursor-pointer items-center justify-center gap-2 bg-zinc-950 px-4 text-xs font-medium text-white transition-colors hover:bg-zinc-800 sm:w-[16rem] sm:min-w-[16rem] sm:max-w-[16rem]">
                      <Upload className="h-3.5 w-3.5" /> Choose File
                    </label>
                    <span className="text-sm text-zinc-500">{d.uploadFile ? 'File selected' : 'No file chosen'}</span>
                  </div>
                  <input
                    key={`ds-${i}-upload-${d.uploadFile?.name || d['Uploaded Files']?.length || 'empty'}`}
                    id={`ds-${i}-upload`}
                    type="file"
                    accept=".csv,.xlsx,.xls,.xlsm"
                    onChange={e => updateDS(i, 'uploadFile', e.target.files?.[0] || null)}
                    className="sr-only"
                    data-testid={`ds-${i}-upload`}
                  />
                </Field>
                {d.uploadFile && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                    <span>Selected file: {d.uploadFile.name} ({Math.ceil(d.uploadFile.size / 1024)} KB)</span>
                    <button
                      type="button"
                      onClick={() => clearDSUpload(i)}
                      className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700"
                      data-testid={`ds-${i}-clear-upload`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove file
                    </button>
                  </div>
                )}
                {!d.uploadFile && d['Uploaded Files']?.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                    <span>Saved uploaded file: {d['Uploaded Files'].map(f => f.original_filename || f.filename || 'uploaded file').join(', ')}</span>
                    <button
                      type="button"
                      onClick={() => clearDSUpload(i)}
                      className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700"
                      data-testid={`ds-${i}-clear-saved-upload`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove file
                    </button>
                  </div>
                )}
                {!d.auto && !d.uploadFile && !d['Uploaded Files']?.length && (
                  <div className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" /> Sample data upload is mandatory for external data sources.
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        <button onClick={addExternal} className="w-full border-2 border-dashed border-zinc-300 px-6 py-4 text-sm text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex items-center justify-center gap-2" data-testid="add-external-ds">
          <Plus className="w-4 h-4" /> Add External Data Source
        </button>
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={nextDisabled} testid="step3-next" />
    </div>
  );
}

function TechStep({ tech, setTech, onNext, onBack, nextDisabled }) {
  return (
    <div className="animate-reveal" data-testid="step-tech">
      <StepHeader num={4} title="Technology Stack" sub="Evaluate your current technology infrastructure and AI-readiness considerations." />
      <div className="grid md:grid-cols-2 gap-5">
        {Object.keys(tech).map(k => (
          <Field key={k} label={k}>
            <select className={inputCls} value={tech[k]} onChange={e => setTech({ ...tech, [k]: e.target.value })} data-testid={`tech-${k.replace(/\s+/g, '-').toLowerCase()}`}>
              {YESNO.map(v => <option key={v}>{v}</option>)}
            </select>
          </Field>
        ))}
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={nextDisabled} testid="step4-next" />
    </div>
  );
}


function LockedGoals({ goals, onNext, onBack }) {
  return <div><StepHeader num={2} title="Goals & Challenges" sub="Goals and priorities are fixed for regeneration. Start a new assessment to change them." />
    {goals.map((goal, i) => <section key={i} className="border border-zinc-200 p-5 mb-4"><h3 className="font-semibold">Priority {goal.Priority}</h3>
      {['Goal Description', 'Challenges', 'Business Priority', 'Success Metrics', 'Data Availability', 'Solution Fit'].map(key => goal[key] ? <div key={key} className="mt-3"><div className="text-xs text-zinc-500">{key}</div><p className="text-sm mt-1 whitespace-pre-wrap">{goal[key]}</p></div> : null)}
    </section>)}<NavButtons onBack={onBack} onNext={onNext} /></div>;
}

export function GenerateStep({ generation, onPayment, paymentAvailable, generating, reports, onGenerate, onBack, activeTab, setActiveTab, reportRef, canGenerate, inputError }) {
  return (
    <div className="animate-reveal" data-testid="step-generate">
      <StepHeader num={5} title="Generate Report" sub="Run the AI analysis on your inputs. Generation will take a few minutes." />
      <GenerationProgress generation={generation} />

      {!reports && (
        <div className="border border-zinc-200 p-10 text-center">
          <FileText className="w-12 h-12 text-[#002FA7] mx-auto mb-4" strokeWidth={1.2} />
          <h3 className="font-display text-xl font-bold text-zinc-950">Ready to generate</h3>
          <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">Generate a synopsis with Business Overview and AI Readiness. Detailed Analysis will be generated after payment.</p>
          <button onClick={onGenerate} disabled={generating || !canGenerate} className="mt-6 bg-[#002FA7] text-white px-8 py-3.5 hover:bg-[#00227A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 text-sm font-medium" data-testid="generate-report-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating analysis…</> : <><Sparkles className="w-4 h-4" /> Generate AI Readiness Report</>}
          </button>
          <ValidationNotice text={inputError?.text} className="mt-5 text-left" />
          {!canGenerate && <p className="mt-3 text-xs text-amber-700">Complete all mandatory fields before generating the report.</p>}
          {generating && (
            <div className="mt-6 text-xs text-zinc-500 font-mono flex items-center justify-center gap-2">
              <span className="pulse-dot inline-block w-1.5 h-1.5 bg-[#002FA7] rounded-full" />
              Processing company data → goals → data sources → tech stack…
            </div>
          )}
        </div>
      )}

      {reports && (
        <div ref={reportRef} className="space-y-6">
          <div className="bg-zinc-950 text-white p-6 flex items-center justify-between" data-testid="report-summary">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">AI Readiness Verdict</div>
              <div className="font-display text-3xl font-bold mt-2">{reports.readiness_level}</div>
            </div>
          </div>

          <div className="flex gap-px bg-zinc-200">
            {[
              { k: 'business_overview', t: 'Business Overview' },
              { k: 'ai_readiness', t: 'AI Readiness' },
            ].map(t => (
              <button key={t.k} onClick={() => setActiveTab(t.k)} className={`flex-1 text-xs uppercase tracking-wider py-3 transition-colors font-medium ${activeTab === t.k ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`} data-testid={`report-tab-${t.k}`}>
                {t.t}
              </button>
            ))}
          </div>

          <div className="border border-zinc-200 p-8 prose-startin" data-testid="report-content">
            <ReportMarkdown text={reports[activeTab === 'ai_readiness' ? 'ai_readiness' : 'business_overview']} />
          </div>
        </div>
      )}

      {reports ? <div className="mt-8 flex justify-end"><button onClick={onPayment} disabled={!paymentAvailable} className="bg-[#002FA7] text-white px-6 py-3 text-sm font-medium disabled:opacity-50">View Detail Report →</button></div> :
        <NavButtons onBack={onBack} onNext={onGenerate} nextLabel="Generate" nextDisabled={generating || !canGenerate} testid="step5-next" />}

    </div>
  );
}
